import React from "react";
import { Card } from "@/components/ui/card";
import { Compass, Layers, AlertTriangle } from "lucide-react";
import ToricDial from "@/components/ToricDial";

function fmt(v, digits = 2) {
  if (v === null || v === undefined) return "—";
  return typeof v === "number" ? v.toFixed(digits) : v;
}

function Row({ label, value, mono = true }) {
  return (
    <div className="flex items-center justify-between py-1.5 border-b border-slate-100 last:border-b-0">
      <span className="text-xs text-slate-500">{label}</span>
      <span className={`text-sm ${mono ? "font-mono" : ""} text-slate-800`}>{value}</span>
    </div>
  );
}

function CristalensBlock({ side, t }) {
  if (!t) return null;
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4">
      <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-2">{side} · Cristalens Toric</div>
      <ToricDial
        steepAxis={t.steep_axis}
        iolAxis={t.iol_alignment_axis}
        incisionAxis={180}
      />
      <div className="mt-3 rounded-md bg-sky-50 border border-sky-200 p-3">
        <div className="text-[11px] uppercase text-sky-800 font-semibold">Recommended</div>
        <div className="font-serif text-lg font-semibold text-sky-900 leading-tight">{t.recommended_model}</div>
        <div className="text-xs text-sky-800 mt-1">
          IOL cyl <span className="font-mono">{fmt(t.recommended_iol_cyl)} D</span> · Align at{" "}
          <span className="font-mono">{fmt(t.iol_alignment_axis, 1)}°</span>
          {t.toric_indicated && (
            <> · residual <span className="font-mono">{fmt(t.residual_astigmatism)} D</span></>
          )}
        </div>
        <CylLadder available={t.available_cylinders} selected={t.toric_indicated ? t.recommended_iol_cyl : null} tone="sky" side={side} />
      </div>
      <RotationGuard guard={t.rotation_guard} side={side} />
      <div className="mt-2 text-xs">
        <Row label="Corneal astigmatism" value={`${fmt(t.corneal_astigmatism)} D @ ${fmt(t.steep_axis, 0)}°`} />
        <Row label="SIA" value={`${fmt(t.sia)} D @ ${fmt(t.sia_axis, 0)}°`} />
        <Row label="Net corneal astigmatism" value={`${fmt(t.net_corneal_astigmatism)} D @ ${fmt(t.net_axis, 1)}°`} />
        <Row label="IOL cyl @ lens plane" value={`${fmt(t.iol_cylinder_at_lens_plane)} D`} />
        <Row label="Incision" value={t.incision_location} mono={false} />
      </div>
    </div>
  );
}

function ArtisBlock({ side, t }) {
  if (!t) return null;
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">{side} · Artis Toric (PCA)</div>
        <span className="text-[10px] uppercase tracking-wider bg-emerald-50 text-emerald-800 border border-emerald-200 rounded-full px-2 py-0.5">PCA ON</span>
      </div>
      <ToricDial
        steepAxis={t.total_corneal_axis}
        iolAxis={t.iol_alignment_axis}
        incisionAxis={180}
        accent="emerald"
      />
      <div className="mt-3 rounded-md bg-emerald-50 border border-emerald-200 p-3">
        <div className="text-[11px] uppercase text-emerald-800 font-semibold">Recommended</div>
        <div className="font-serif text-lg font-semibold text-emerald-900 leading-tight">{t.recommended_model}</div>
        <div className="text-xs text-emerald-800 mt-1">
          IOL cyl <span className="font-mono">{fmt(t.recommended_iol_cyl)} D</span> · Align at{" "}
          <span className="font-mono">{fmt(t.iol_alignment_axis, 1)}°</span> · Residual{" "}
          <span className="font-mono">{fmt(t.residual_astigmatism)} D</span>
        </div>
        <CylLadder available={t.available_cylinders} selected={t.toric_indicated ? t.recommended_iol_cyl : null} tone="emerald" side={side} />
        <div className="text-xs text-emerald-900 mt-2">IOL placement: <span className="font-medium">{t.iol_placement}</span></div>
      </div>
      <RotationGuard guard={t.rotation_guard} side={side} variant="artis" />
      <div className="mt-2 text-xs">
        <Row label="Anterior astigmatism" value={`${fmt(t.anterior_astigmatism)} D @ ${fmt(t.anterior_axis, 0)}°`} />
        <Row label="Posterior (Abulafia-Koch)" value={`${fmt(t.posterior_astigmatism)} D @ ${fmt(t.posterior_axis, 0)}°`} />
        <Row label="Total corneal astigmatism" value={`${fmt(t.total_corneal_astigmatism)} D @ ${fmt(t.total_corneal_axis, 1)}°`} />
        <Row label="SIA" value={`${fmt(t.sia)} D @ ${fmt(t.sia_axis, 0)}°`} />
        <Row label="Net to treat" value={`${fmt(t.net_astigmatism_to_treat)} D @ ${fmt(t.net_axis, 1)}°`} />
      </div>
    </div>
  );
}

export default function ToricCards({ result }) {
  return (
    <Card className="p-5 sm:p-6 border-slate-200" data-testid="toric-cards">
      <div className="flex items-center gap-2 mb-4">
        <Compass className="w-5 h-5 text-sky-700" />
        <h2 className="font-serif text-2xl font-semibold tracking-tight">Toric Calculators</h2>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <CristalensBlock side="OD" t={result.od?.cristalens_toric} />
        <CristalensBlock side="OS" t={result.os?.cristalens_toric} />
        <ArtisBlock side="OD" t={result.od?.artis_toric} />
        <ArtisBlock side="OS" t={result.os?.artis_toric} />
      </div>
    </Card>
  );
}

function CylLadder({ available, selected, tone, side }) {
  if (!available?.length) return null;
  const on = tone === "sky" ? "bg-sky-700 text-white border-sky-700" : "bg-emerald-700 text-white border-emerald-700";
  return (
    <div className="mt-2 flex flex-wrap gap-1" data-testid={`${side.toLowerCase()}-${tone}-cyl-ladder`}>
      {available.map((c) => (
        <span
          key={c}
          className={`px-1.5 py-0.5 rounded border text-[10px] font-mono ${c === selected ? on : "bg-white text-slate-500 border-slate-200"}`}
          data-testid={c === selected ? `${side.toLowerCase()}-${tone}-cyl-selected` : undefined}
        >
          +{c.toFixed(2)}
        </span>
      ))}
    </div>
  );
}

function RotationGuard({ guard, side, variant = "cristalens" }) {
  if (!guard?.near_incision) return null;
  return (
    <div
      className="mt-2 rounded-md border border-amber-300 bg-amber-50 p-2.5 text-xs text-amber-900 flex gap-2"
      data-testid={`${side.toLowerCase()}-${variant}-rotation-guard`}
    >
      <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-amber-600" />
      <div>
        <div className="font-semibold">Toric axis within {guard.threshold_deg}° of the incision ({guard.distance_deg}°)</div>
        <div className="mt-0.5 leading-snug">{guard.message}</div>
      </div>
    </div>
  );
}
